import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import EditIcon from '@mui/icons-material/Edit';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { SpeedDial } from '../../components/SpeedDial';
import { RightDrawer } from '../../components/RightDrawer';
import { FormVariant } from '../../forms';
import { ThirdPartyFormTab } from '../../forms/ThirdParty/enums/tab.enum';
import { PersonForm } from '../../forms/ThirdParty/Person';
import { Billing as BillingCmp } from '../../components/Billing';

export const PersonPage_Billing_Person_Fragment = graphql(`
  fragment PersonPage_Billing_Person_Fragment on Person {
    _id
    isInternal
    billReceiver {
      kind
      ... on Person {
        ...PersonCardCmp_Person_Fragment
      }
      ... on Organization {
        ...OrganizationCardCmp_Organization_Fragment
      }
    }
    billEmitters {
      _id
      kind
      ... on Person {
        ...PersonCardCmp_Person_Fragment
      }
      ... on Organization {
        ...OrganizationCardCmp_Organization_Fragment
      }
    }
    ...BillingCmp_ThirdParty_Fragment
    ...ThirdPartyForm_Person_Fragment
  }
`);

interface Props {
  readonly paths: Readonly<{
    consult: string;
    edit: string;
  }>;
  readonly person: FragmentType<typeof PersonPage_Billing_Person_Fragment>;
}

export const Billing = ({ paths, person: pPerson }: Readonly<Props>) => {
  const person = getFragmentData(PersonPage_Billing_Person_Fragment, pPerson);

  const { pathname: currentPath } = useLocation();
  const navigate = useNavigate();

  const isInEditionMode = currentPath === paths.edit;
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  return (
    <>
      <BillingCmp thirdParty={person} />
      <SpeedDial
        actions={[
          {
            id: 'editPersonBilling',
            icon: <EditIcon />,
            name: 'Modifier Infos Facturation',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur les Informations de Facturation de la Personne"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <PersonForm
          formVariant={FormVariant.Modification}
          initialValues={person}
          isInternal={person.isInternal}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          personId={person._id}
          stepIdsSubset={[
            ThirdPartyFormTab.BillEmitters,
            ThirdPartyFormTab.BillReceiver,
          ]}
          title="Formulaire de modification des Informations de Facturation de la Personne"
        />
      </RightDrawer>
    </>
  );
};
